import { dispatchToast } from "./htmx-helpers";
import { boostedNavigate } from "./navigate";
import { navProgress } from "./nav-progress";

// ---------------------------------------------------------------------------
// Wiki navigation save-gate.
//
// Every wiki navigation target (sidebar tree, backlinks, in-editor wikilinks)
// is a real `<a href>` boosted by <body hx-boost="true">, so htmx pushes the URL
// into history like any other boosted link: Back/Forward work, reload stays on
// the page, and cmd/ctrl/middle-click open a new tab natively (htmx 1.9 skips
// boosted anchors on ctrl/meta click; middle-click is `auxclick`, never seen).
//
// The only thing JS adds: links carrying `data-wiki-save-before` (or sitting
// inside an element that does) are held at `htmx:confirm` while the mounted
// wiki editor has unsaved work. The editor is flushed (content PATCH + link
// sync), then the ORIGINAL boosted request is issued once — so history handling
// stays htmx's own. Failure policy: a failed save never navigates silently; the
// user gets an error toast and stays on the page. Clicking the same destination
// again retries the save and, if that fails too, leaves anyway (explicit
// override — the toast said so), so a permanent conflict can't trap them.
//
// New-tab clicks bypass the gate by design: the current tab keeps the editor
// open and its debounce/unload flush still persists the edits.
// ---------------------------------------------------------------------------

export const WIKI_NAV_ATTR = "data-wiki-save-before";

export interface WikiNavEditor {
  /** True while there is anything not yet persisted (dirty, debouncing, in flight, links unsynced). */
  hasPendingChanges(): boolean;
  /** Persist everything now. Resolves true when the content is saved. */
  flush(): Promise<boolean>;
  /** Localized error toast shown when a navigation is blocked by a failed save. */
  saveFailedMessage: string;
}

type IssueRequest = (skipConfirmation?: boolean) => unknown;
interface HeldRequest {
  elt: Element;
  path: string;
  issue: IssueRequest;
}

/**
 * A held save that hasn't settled after this long counts as NOT saved: the
 * user gets the error toast and can leave with a second click. Without it a
 * hung PATCH (fetch has no timeout) would hold navigation indefinitely.
 */
export const WIKI_NAV_SAVE_TIMEOUT_MS = 15_000;

let activeEditor: WikiNavEditor | null = null;
let flushing = false;
let latest: HeldRequest | null = null;
let overridePath: string | null = null;
// Bumped whenever the editor is torn down (the page was left by any route —
// an ungated app link, Back, a sidebar refresh). A held flush captures it at
// start; if it changed by the time the save settles, the held navigation is
// stale and is dropped — it must never yank the user back to the wiki page or
// toast on the page they went to.
let generation = 0;
// Nav progress bar key for the currently held navigation. The bar normally
// starts on htmx:beforeSend, which a held request hasn't reached — so the gate
// starts it under this key when it holds a click and finishes it however the
// hold ends. On success the issued request's XHR key is started (synchronously,
// inside issueRequest) BEFORE this one is finished, so the bar continues.
let heldProgressKey: object | null = null;

function startHeldProgress(): object {
  if (!heldProgressKey) {
    heldProgressKey = {};
    navProgress.start(heldProgressKey);
  }
  return heldProgressKey;
}

function finishHeldProgress(key: object) {
  if (heldProgressKey === key) heldProgressKey = null;
  navProgress.finish(key); // no-op when already finished
}

export function setActiveWikiEditor(editor: WikiNavEditor) {
  activeEditor = editor;
  overridePath = null;
}

export function clearActiveWikiEditor(editor: WikiNavEditor) {
  if (activeEditor !== editor) return;
  activeEditor = null;
  generation++;
  flushing = false;
  latest = null;
  overridePath = null;
  // The page was left some other way (its own XHR drives the bar now): the
  // held navigation is dead, so is its share of the bar.
  if (heldProgressKey) finishHeldProgress(heldProgressKey);
}

function requestPath(elt: Element, detailPath: unknown): string {
  if (typeof detailPath === "string" && detailPath) return detailPath;
  return elt.getAttribute("href") || "";
}

// Starts the flush synchronously (the PATCH must leave before any swap) and
// maps a throw/rejection to "not saved".
function startFlush(editor: WikiNavEditor): Promise<boolean> {
  let saved: Promise<boolean>;
  try {
    saved = editor.flush().then(
      (ok) => ok === true,
      () => false,
    );
  } catch {
    return Promise.resolve(false);
  }
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<boolean>((resolve) => {
    timer = setTimeout(() => resolve(false), WIKI_NAV_SAVE_TIMEOUT_MS);
  });
  return Promise.race([saved, timeout]).finally(() => clearTimeout(timer));
}

function proceed(req: HeldRequest) {
  if (req.elt.isConnected) {
    req.issue(true);
    return;
  }
  // htmx silently drops requests whose element left the DOM (e.g. a sidebar
  // refresh swapped the link out during the save) — re-navigate by URL.
  if (req.path) navigateWiki(req.path, { gated: false });
}

/** htmx:confirm handler. Returns true when it held the request back. */
export function wikiNavGate(evt: Event): boolean {
  const d = ((evt as CustomEvent).detail || {}) as Record<string, unknown>;
  const elt = d.elt as Element | undefined;
  if (!elt || typeof elt.closest !== "function" || !elt.closest(`[${WIKI_NAV_ATTR}]`)) return false;
  if (typeof d.issueRequest !== "function") return false;
  if (typeof d.verb === "string" && d.verb.toLowerCase() !== "get") return false;
  const editor = activeEditor;
  if (!editor || !editor.hasPendingChanges()) return false;

  evt.preventDefault();
  latest = { elt, path: requestPath(elt, d.path), issue: d.issueRequest as IssueRequest };
  const progressKey = startHeldProgress(); // coalesced clicks share it
  if (flushing) return true; // coalesce: the running flush issues the latest request

  flushing = true;
  const gen = generation;
  startFlush(editor).then((saved) => {
    // The editor was torn down meanwhile: the user already left. Drop the
    // held request without touching state that may belong to a newer editor
    // (its bar key was already finished by clearActiveWikiEditor; finishing
    // this flush's own key again is a no-op and never touches a newer hold).
    if (gen !== generation) {
      navProgress.finish(progressKey);
      return;
    }
    flushing = false;
    const req = latest;
    latest = null;
    if (!req) {
      finishHeldProgress(progressKey);
      return;
    }
    if (saved || (overridePath !== null && overridePath === req.path)) {
      overridePath = null;
      proceed(req); // starts the request's own XHR key first...
      finishHeldProgress(progressKey); // ...so this hands the bar over
      return;
    }
    overridePath = req.path;
    finishHeldProgress(progressKey);
    dispatchToast(editor.saveFailedMessage, "error");
  });
  return true;
}

/**
 * Programmatic wiki navigation that behaves exactly like clicking a sidebar
 * link: a real boosted anchor (history push, body swap) that passes through
 * the save gate unless `gated: false`. Used by in-editor wikilink clicks.
 */
export function navigateWiki(url: string, opts: { gated?: boolean } = {}) {
  boostedNavigate(url, opts.gated !== false ? { [WIKI_NAV_ATTR]: "" } : {});
}

/**
 * Back/Forward (popstate) can't be held — htmx refetches immediately — but it
 * fires htmx:beforeHistorySave first, so start the flush while the old page's
 * editor still exists. The PATCH races the history GET; best effort, same as
 * the unload flush.
 */
export function flushWikiEditorBeforeHistorySave() {
  const editor = activeEditor;
  if (!editor || !editor.hasPendingChanges()) return;
  void startFlush(editor);
}

/**
 * In-editor wikilink follow (markdown-editor wikilinks.ts dispatches
 * `wiki-open-link` with the link's title). Wikilink contract: the markup is
 * [[Page Title]] and only the server maps a title to a page — so the title is
 * resolved against the editor's OWN wiki (data-wiki-resolve-url, rendered from
 * the same scope as data-wiki-base) and the returned canonical URL is opened
 * with the same gated boosted navigation as a sidebar click. The progress bar
 * covers the resolve round-trip and hands over to the navigation.
 */
export async function onWikiOpenLink(evt: Event): Promise<void> {
  const title = ((evt as CustomEvent).detail || {}).title;
  const source = evt.target as Element | null;
  if (typeof title !== "string" || !title || typeof source?.closest !== "function") return;
  const root = source.closest<HTMLElement>("[data-wiki-resolve-url]");
  const resolveUrl = root?.dataset.wikiResolveUrl;
  if (!root || !resolveUrl) return;

  const key = {};
  navProgress.start(key);
  try {
    const resp = await fetch(`${resolveUrl}?title=${encodeURIComponent(title)}`, {
      headers: { Accept: "application/json" },
    });
    // The editor was left while resolving: never yank the user back.
    if (!source.isConnected) return;
    if (resp.ok) {
      const url = ((await resp.json()) as { url?: unknown }).url;
      if (typeof url === "string" && url && source.isConnected) navigateWiki(url);
      return;
    }
    if (resp.status === 404) {
      dispatchToast(root.dataset.wikiLinkMissing || "", "error");
      return;
    }
    console.warn("wiki: resolve link failed", resp.status);
  } catch (err) {
    console.warn("wiki: resolve link failed", err);
  } finally {
    navProgress.finish(key); // after navigateWiki started its own request/hold
  }
}

if (typeof document !== "undefined") {
  document.addEventListener("htmx:confirm", wikiNavGate);
  document.addEventListener("wiki-open-link", onWikiOpenLink);
  document.addEventListener("htmx:beforeHistorySave", flushWikiEditorBeforeHistorySave);
}
